/*
 * Which country's store this browser is shopping in.
 *
 * The backend decides what a store sells and at what price
 * (marketplace/market.py): a request names its market with ?market=XX and
 * anything missing is India. The web only has to pick the market and send it.
 * (?market= rather than the X-Fleek-Country header the apps use, so no CORS
 * preflight change is needed.)
 *
 * Off unless the server switch is on: config/app_update?os=web answers
 * regions_enabled, the same switch the apps read, and anything but exactly
 * true means India for everyone. ?market=us turns markets on for this tab only
 * (sessionStorage), so a store can be tried in prod before the switch flips;
 * ?market=in goes back.
 *
 * Choosing, in order: a ?market= link, the visitor's own pick (saved), the
 * country Cloudflare sees (web.getfleek.app/cdn-cgi/trace, cached), India.
 *
 * Any country is a store. The backend prices every country outside India in
 * dollars, or in its own currency once switched (LOCAL_CURRENCY_STORES:
 * euros in Germany, pounds in the UK), and sells it what is tagged GLOBAL, for that country or for a region
 * containing it (market.py market_currency / covering_codes), so the UK gets
 * the global catalogue plus anything tagged GB or EUROPE. MARKETS below are
 * the stores the switcher always offers; every other real country code is a
 * store too, named by the browser. Their `currency` is only a label for the
 * picker: once a page has loaded a store's prices, marketCurrency() answers
 * the currency the API priced them in (noteStoreCurrency).
 */

import Api from "./Api/Api";
import countryStores from "../seo/country-stores.json";

export const INDIA = "IN";

// Countries no store is offered in, as market.py BLOCKED.
const BLOCKED = new Set(["CU", "IR", "KP", "SY"]);
// Region codes the browser can name that are not countries.
const NOT_COUNTRIES = new Set(["EU", "EZ", "QO", "UN", "XA", "XB", "ZZ"]);
const CODE = /^[A-Z]{2}$/;

// The stores the switcher always lists, India and the US first.
const PINNED = {
  IN: { label: "India", currency: "inr", flag: "🇮🇳" },
  US: { label: "United States", currency: "usd", flag: "🇺🇸" },
  AE: { label: "United Arab Emirates", currency: "usd", flag: "🇦🇪" },
  SA: { label: "Saudi Arabia", currency: "usd", flag: "🇸🇦" },
  QA: { label: "Qatar", currency: "usd", flag: "🇶🇦" },
  KW: { label: "Kuwait", currency: "usd", flag: "🇰🇼" },
  BH: { label: "Bahrain", currency: "usd", flag: "🇧🇭" },
  OM: { label: "Oman", currency: "usd", flag: "🇴🇲" },
};

const CHOICE_KEY = "fleek_market"; // the visitor's own pick
const GEO_KEY = "fleek_geo_country"; // what Cloudflare saw, any country
const PREVIEW_KEY = "fleek_market_preview"; // this tab only
const SWITCH_KEY = "fleek_regions_enabled"; // the server switch, as last seen
const BLOCKED_TAB_KEY = "fleek_market_blocked"; // a blocked country's ?market= link, this tab only
const NOTICE_KEY = "fleek_market_noticed"; // the last store the one-time notice was shown for
const CURRENCY_KEY = "fleek_store_currency"; // {"DE": "eur"}: each store's currency as its prices came back

const store = (fn, fallback = null) => {
  try {
    return fn();
  } catch (e) {
    return fallback;
  }
};

const regionNames = store(() => new Intl.DisplayNames(["en"], { type: "region", fallback: "none" }));

/** "United Kingdom" for GB; undefined when the browser can't name it. */
const regionName = (code) => store(() => regionNames.of(code)) || undefined;

// 🇬🇧 from GB: two regional-indicator letters.
const flagOf = (code) => String.fromCodePoint(...[...code].map((c) => 0x1f1a5 + c.charCodeAt(0)));

/**
 * The stores the switcher lists: India and the US first, then the Gulf and
 * every country with a store of its own (src/seo/country-stores.json, the
 * same list that decides which countries get brand pages), by name. Any other
 * country is still a store; the switcher adds the visitor's own at the top.
 */
export const MARKETS = (() => {
  const out = { IN: PINNED.IN, US: PINNED.US };
  const rest = new Set([...Object.keys(PINNED), ...((countryStores && countryStores.countries) || [])]);
  rest.delete("IN");
  rest.delete("US");
  [...rest]
    .filter((code) => CODE.test(code) && !BLOCKED.has(code))
    .map((code) => [code, PINNED[code] || { label: regionName(code) || code, currency: "usd", flag: flagOf(code) }])
    .sort((a, b) => a[1].label.localeCompare(b[1].label))
    .forEach(([code, info]) => {
      out[code] = info;
    });
  return out;
})();

/**
 * A market code, or null. Any real country is a store; blocked countries,
 * region codes and codes the browser can't name are not. (A browser without
 * Intl.DisplayNames takes any well-formed code; the backend copes.)
 */
export const cleanMarket = (value) => {
  const code = String(value || "").trim().toUpperCase();
  if (MARKETS[code]) return code;
  if (!CODE.test(code) || BLOCKED.has(code) || NOT_COUNTRIES.has(code)) return null;
  if (regionNames && !regionName(code)) return null;
  return code;
};

/** Label, currency and flag for a store: MARKETS', or a dollar store named by the browser. */
export const marketInfo = (code) => {
  const clean = cleanMarket(code);
  if (!clean) return MARKETS[INDIA];
  return MARKETS[clean] || { label: regionName(clean) || clean, currency: "usd", flag: flagOf(clean) };
};

/** A blocked country's code (CU, IR, KP, SY), or null. */
const blockedOf = (value) => {
  const code = String(value || "").trim().toUpperCase();
  return BLOCKED.has(code) ? code : null;
};

/** The country's name for a code, e.g. "Iran" for IR. */
export const countryName = (code) => regionName(String(code || "").toUpperCase()) || String(code || "");

/**
 * A ?market= link: sets this tab's preview and the visitor's pick. A blocked
 * country's code shows this tab the "not available" page instead of a store.
 */
export const readMarketLink = (search = window.location.search) => {
  const raw = store(() => new URLSearchParams(search).get("market"));
  const blocked = blockedOf(raw);
  if (blocked) {
    store(() => sessionStorage.setItem(BLOCKED_TAB_KEY, blocked));
    return blocked;
  }
  const code = cleanMarket(raw);
  if (!code) return null;
  store(() => sessionStorage.removeItem(BLOCKED_TAB_KEY));
  store(() => sessionStorage.setItem(PREVIEW_KEY, code === INDIA ? "" : "1"));
  store(() => localStorage.setItem(CHOICE_KEY, code));
  return code;
};

// A store's own page: its home (/us), a category hub (/us/category/ai-tools)
// or a brand page (/us/brand/steam). India's pages carry no prefix.
const STORE_PAGE = /^\/([a-z]{2})(\/(?:brand|category)\/[^/]+)?\/?$/i;

/**
 * A store's own page (src/seo/country-stores.json) opens in that store, as a
 * ?market=us link would: the page and its prices are the US store's, for
 * visitors and crawlers alike. Returns the code, or null.
 */
export const readMarketPath = (pathname = window.location.pathname) => {
  const m = STORE_PAGE.exec(String(pathname || ""));
  if (!m || m[1].toUpperCase() === INDIA || !cleanMarket(m[1])) return null;
  return readMarketLink(`?market=${m[1].toLowerCase()}`);
};

/**
 * The same page without the store prefix (/us/brand/steam → /brand/steam,
 * /us → /), or null when this is not a store's own page. Changing store goes
 * there, since the prefixed page would open its own store again.
 */
export const unprefixedStorePath = (pathname = window.location.pathname) => {
  const m = STORE_PAGE.exec(String(pathname || ""));
  if (!m || !cleanMarket(m[1]) || m[1].toUpperCase() === INDIA) return null;
  return m[2] || "/";
};

const HINT_DISMISSED_KEY = "fleek_wrong_store_hint"; // this tab only

/**
 * "Shopping from the UAE? See the UAE store": on a store's own page (/us/...)
 * opened by someone whose country has a store of its own (MARKETS: India,
 * the US, the Gulf and every country store). The address chose the store,
 * not the visitor, so say so once and offer theirs, without redirecting
 * (search engines and shared links keep working). Never for crawlers, a
 * blocked country, or a visitor already in their own store. Returns
 * { code, label, flag } or null.
 */
export const wrongStoreHint = (pathname = window.location.pathname) => {
  if (isCrawler() || isBlocked()) return null;
  if (!unprefixedStorePath(pathname)) return null;
  if (store(() => sessionStorage.getItem(HINT_DISMISSED_KEY) === "1", false)) return null;
  const home = cleanMarket(knownGeo());
  if (!home || home === current || !MARKETS[home]) return null;
  const info = marketInfo(home);
  return { code: home, label: info.label, flag: info.flag };
};

/** The visitor dismissed the hint: not again in this tab. */
export const dismissWrongStoreHint = () => store(() => sessionStorage.setItem(HINT_DISMISSED_KEY, "1"));

/** Go to this page in the visitor's own store (the hint's link). */
export const goToOwnStore = (code, pathname = window.location.pathname) => {
  chooseMarket(code);
  window.location.assign(unprefixedStorePath(pathname) || "/");
};

/** The brand slug of a store's own brand page, or null (see readMarketPath). */
export const storeBrandSlug = (pathname = window.location.pathname) => {
  const m = /^\/[a-z]{2}\/brand\/([^/]+)\/?$/i.exec(String(pathname || ""));
  return m ? m[1] : null;
};

export const previewOn = () => store(() => sessionStorage.getItem(PREVIEW_KEY) === "1", false);

/** The market to use now, from what is already known (no network). */
export const pickMarket = ({ enabled, choice, geo }) => {
  if (!enabled) return INDIA;
  return cleanMarket(choice) || cleanMarket(geo) || INDIA;
};

let current = INDIA;

/** Whether this browser may leave India's store at all (switch on, or a preview tab). */
export const marketsEnabled = () => previewOn() || savedSwitch();

/** The market every catalogue and payment call is for. */
export const getMarket = () => current;
export const isIndia = () => current === INDIA;
/**
 * A blocked country (Cloudflare's, or a ?market= link's): no store at all,
 * not India's either. The app shows "not available in your country".
 * Independent of the server switch.
 */
export const isBlocked = () => BLOCKED.has(current);

const learnedCurrencies = () =>
  store(() => {
    const saved = JSON.parse(localStorage.getItem(CURRENCY_KEY) || "{}");
    return saved && typeof saved === "object" ? saved : {};
  }, {});

/**
 * A store's currency: what its prices last came back in from the API
 * (noteStoreCurrency), else the picker's label. India is always rupees.
 */
export const marketCurrency = (code = current) => {
  const clean = cleanMarket(code);
  const learned = clean && clean !== INDIA ? learnedCurrencies()[clean] : null;
  // Rupees are India's alone; a browser that learned "inr" for another store
  // (before noteStoreCurrency refused it) falls back to the picker's label.
  const usable = typeof learned === "string" && /^[a-z]{3}$/.test(learned) && learned !== "inr";
  return usable ? learned : marketInfo(code).currency;
};

/**
 * Remember the currency the API priced a store in (a plan's currency_type,
 * or a listing's starts_from), so the store picker, the store notice and any
 * price the page formats itself say that currency, not the picker's label.
 * India's is never learned: it is rupees, and its listings can carry dollar
 * plans. Nor are rupees learned for any other store: no store outside India
 * is priced in them, but an affiliate plan keeps its brand's own price and
 * currency everywhere (#343), and one rupee price used to turn the US store's
 * chip to "₹".
 */
export const noteStoreCurrency = (currency, code = current) => {
  const clean = cleanMarket(code);
  const type = String(currency || "").trim().toLowerCase();
  if (!clean || clean === INDIA || !/^[a-z]{3}$/.test(type) || type === "inr") return;
  const saved = learnedCurrencies();
  if (saved[clean] === type) return;
  store(() => localStorage.setItem(CURRENCY_KEY, JSON.stringify({ ...saved, [clean]: type })));
};

const savedChoice = () => store(() => localStorage.getItem(CHOICE_KEY));
const savedGeo = () => store(() => localStorage.getItem(GEO_KEY));
// The server switch as the last visit saw it; on a first visit, as of the
// build (scripts/prerender-meta.js writes <meta name="fleek-regions">).
const savedSwitch = () =>
  store(() => {
    const saved = localStorage.getItem(SWITCH_KEY);
    if (saved === "1" || saved === "0") return saved === "1";
    return document.querySelector('meta[name="fleek-regions"]')?.getAttribute("content") === "on";
  }, false);

// Cloudflare's country, handed over by nginx as a cookie on the page itself
// (.nginx/nginx.conf), so a first visit knows it without a round trip.
const cookieGeo = () =>
  store(() => {
    const m = /(?:^|;\s*)fleek_cf_country=([A-Z]{2})(?:;|$)/.exec(document.cookie || "");
    return m ? m[1] : null;
  });

// Search and social crawlers always get India's store: India's pages are the
// ones indexed (canonical, prerendered), and Googlebot crawls from the US.
// Named crawlers only: a bare "bot" would catch phones (CUBOT) too.
const CRAWLER =
  /googlebot|google-inspectiontool|googleother|adsbot-google|mediapartners-google|bingbot|duckduckbot|yandex|baiduspider|slurp|applebot|petalbot|ahrefsbot|semrushbot|gptbot|oai-searchbot|claudebot|perplexitybot|facebookexternalhit|twitterbot|linkedinbot|slackbot|discordbot|telegrambot|whatsapp|crawler|spider|lighthouse|headlesschrome/i;
export const isCrawler = (ua = typeof navigator !== "undefined" ? navigator.userAgent : "") => CRAWLER.test(ua || "");

/** The visitor's country: Cloudflare's, fresh from this page, else as saved. */
const knownGeo = () => cookieGeo() || savedGeo();

/**
 * Synchronous start, before first render: a ?market= link, else what the last
 * visit settled (switch, pick, country). No network, so nobody waits.
 */
export const startMarket = (search = window.location.search, pathname = window.location.pathname) => {
  const linked = readMarketLink(search) || readMarketPath(pathname);
  if (isCrawler() && !linked) {
    current = INDIA;
    return current;
  }
  const blocked = blockedOf(store(() => sessionStorage.getItem(BLOCKED_TAB_KEY))) || blockedOf(knownGeo());
  current = blocked || pickMarket({ enabled: previewOn() || savedSwitch(), choice: savedChoice(), geo: knownGeo() });
  return current;
};

export const fetchGeoCountry = async () => {
  const res = await fetch("/cdn-cgi/trace", { cache: "no-store" });
  if (!res.ok) return null;
  const loc = /^loc=([A-Z]{2})$/m.exec(await res.text());
  return loc ? loc[1] : null;
};

const fetchRegionsEnabled = async () => {
  const res = await Api.GetAppUpdate();
  return res?.data?.regions_enabled === true;
};

/**
 * After first render: ask the server switch and Cloudflare, and answer the
 * market this visitor should be in. The caller reloads if it differs from
 * getMarket(), so India visitors (the switch off, or Cloudflare says IN)
 * never reload and never wait.
 */
export const settleMarket = async ({ regions = fetchRegionsEnabled, geo = fetchGeoCountry } = {}) => {
  // A cached blocked country is asked again, so someone who has since left it
  // (or switched off a VPN) gets a store back.
  if (isCrawler()) return current;
  const fresh = cookieGeo();
  const cached = savedGeo();
  const [enabled, country] = await Promise.all([
    regions().catch(() => false),
    fresh
      ? Promise.resolve(fresh)
      : cached && !blockedOf(cached)
        ? Promise.resolve(cached)
        : geo().catch(() => cached || null),
  ]);
  if (country) store(() => localStorage.setItem(GEO_KEY, country));
  store(() => localStorage.setItem(SWITCH_KEY, enabled ? "1" : "0"));
  const blocked = blockedOf(store(() => sessionStorage.getItem(BLOCKED_TAB_KEY))) || blockedOf(country);
  if (blocked) return blocked;
  return pickMarket({ enabled: enabled || previewOn(), choice: savedChoice(), geo: country });
};

/** The visitor picks a store. They chose it, so it needs no notice. */
export const chooseMarket = (code) => {
  const clean = cleanMarket(code);
  if (!clean) return;
  store(() => localStorage.setItem(CHOICE_KEY, clean));
  store(() => localStorage.setItem(NOTICE_KEY, clean));
  current = clean;
};

/**
 * Whether to show "Showing the United Kingdom store, prices in USD · Change":
 * once per store, when a store outside India was chosen for the visitor (by
 * Cloudflare's country or a ?market= link) rather than picked by them.
 */
export const storeNoticeDue = () =>
  marketsEnabled() && !isIndia() && !isBlocked() && store(() => localStorage.getItem(NOTICE_KEY)) !== current;

/** The notice has been shown for this store; never again for it. */
export const markStoreNoticed = () => store(() => localStorage.setItem(NOTICE_KEY, current));

// Calls whose answer depends on the store (market.py's callers).
const MARKET_PATHS = /^\/?(marketplace\/|payments\/v4\/|misc\/v2\/|users\/v5\/)/;

/** axios request interceptor: adds ?market= outside India. */
export const withMarket = (config) => {
  if (current === INDIA) return config;
  const url = String(config?.url || "");
  const relative = url.replace(/^https?:\/\/[^/]+/, "");
  if (!MARKET_PATHS.test(relative)) return config;
  return { ...config, params: { ...(config.params || {}), market: current } };
};
